/**
 * useChat Hook — Chat state management with SSE streaming
 *
 * Manages conversations, message history, and real-time streaming
 * for the Chat About Candidate feature.
 */

import { useState, useCallback, useRef } from 'react';
import { api } from '../api/client';

const BASE_URL = import.meta.env.VITE_API_URL || '';

interface ChatConversation {
  id: number;
  applicationId: number;
  title: string | null;
  modelTier: string;
  messageCount: number;
  totalCostUsd: number;
  hasStaleContext: boolean;
  staleReason: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ChatMessage {
  id: number;
  conversationId: number;
  role: string;
  content: string;
  modelUsed: string | null;
  costUsd: number | null;
  latencyMs: number | null;
  status: string;
  createdAt: string;
}

interface ChatSuggestions {
  questions: string[];
  commands: Array<{ command: string; description: string }>;
}

export function useChat(applicationId: number) {
  const [conversations, setConversations] = useState<ChatConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<ChatConversation | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<ChatSuggestions | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const result = await api.getChatConversations(applicationId);
      setConversations(result.data);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, [applicationId]);

  const loadMessages = useCallback(async (conversationId: number) => {
    try {
      const result = await api.getChatMessages(conversationId);
      setMessages(result.data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }, []);

  const loadSuggestions = useCallback(async (conversationId: number) => {
    try {
      const result = await api.getChatSuggestions(conversationId);
      setSuggestions({ questions: result.data, commands: result.commands });
    } catch (err) {
      console.error('Failed to load suggestions:', err);
      setSuggestions(null);
    }
  }, []);

  const createConversation = useCallback(async (modelTier: 'standard' | 'premium' = 'standard') => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.createChatConversation(applicationId, modelTier);
      const newConv: ChatConversation = {
        id: result.id,
        applicationId: result.applicationId,
        title: null,
        modelTier: result.modelTier,
        messageCount: 0,
        totalCostUsd: 0,
        hasStaleContext: false,
        staleReason: null,
        createdAt: result.createdAt,
        updatedAt: result.createdAt,
      };
      setActiveConversation(newConv);
      setConversations(prev => [newConv, ...prev]);
      setMessages([]);
      loadSuggestions(newConv.id);
      return newConv;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create conversation';
      setError(msg);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [applicationId]);

  const selectConversation = useCallback(async (conversationId: number) => {
    const conv = conversations.find(c => c.id === conversationId);
    if (conv) {
      setActiveConversation(conv);
      await loadMessages(conversationId);
      if (conv.messageCount === 0) {
        loadSuggestions(conversationId);
      } else {
        setSuggestions(null);
      }
    }
  }, [conversations, loadMessages, loadSuggestions]);

  const sendMessage = useCallback(async (content: string) => {
    if (!activeConversation || isStreaming) return;

    setIsStreaming(true);
    setStreamingContent('');
    setError(null);
    setSuggestions(null); // Clear suggestions once user sends a message

    // Optimistically add user message
    const tempUserMsg: ChatMessage = {
      id: Date.now(),
      conversationId: activeConversation.id,
      role: 'user',
      content,
      modelUsed: null,
      costUsd: null,
      latencyMs: null,
      status: 'complete',
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const url = `${BASE_URL}/api/chat/conversations/${activeConversation.id}/messages`;
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;

          try {
            const event = JSON.parse(trimmed.slice(6));

            if (event.type === 'delta' && event.content) {
              accumulated += event.content;
              setStreamingContent(accumulated);
            } else if (event.type === 'done') {
              // Finalize: add assistant message, clear streaming
              const assistantMsg: ChatMessage = {
                id: event.messageId || Date.now() + 1,
                conversationId: activeConversation.id,
                role: 'assistant',
                content: accumulated,
                modelUsed: null,
                costUsd: event.usage?.costUsd ?? null,
                latencyMs: null,
                status: 'complete',
                createdAt: new Date().toISOString(),
              };
              setMessages(prev => [...prev, assistantMsg]);
              setStreamingContent('');

              // Update conversation in list
              setActiveConversation(prev => prev ? {
                ...prev,
                messageCount: prev.messageCount + 2,
                totalCostUsd: prev.totalCostUsd + (event.usage?.costUsd ?? 0),
                hasStaleContext: false,
                updatedAt: new Date().toISOString(),
              } : prev);
            } else if (event.type === 'error') {
              setError(event.error || 'AI service error');
            }
          } catch {
            // Skip unparseable lines
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        const msg = err instanceof Error ? err.message : 'Failed to send message';
        setError(msg);
      }
    } finally {
      setIsStreaming(false);
      setStreamingContent('');
      abortRef.current = null;
    }
  }, [activeConversation, isStreaming]);

  const deleteConversation = useCallback(async (conversationId: number) => {
    try {
      await api.deleteChatConversation(conversationId);
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    }
  }, [activeConversation]);

  const saveInsight = useCallback(async (messageIds?: number[]) => {
    if (!activeConversation) return null;
    try {
      const result = await api.saveChatInsight(activeConversation.id, messageIds);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save insight';
      setError(msg);
      return null;
    }
  }, [activeConversation]);

  const stopStreaming = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
    }
  }, []);

  return {
    conversations,
    activeConversation,
    messages,
    isStreaming,
    streamingContent,
    error,
    isLoading,
    suggestions,
    loadConversations,
    createConversation,
    selectConversation,
    sendMessage,
    saveInsight,
    deleteConversation,
    stopStreaming,
  };
}
